import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import SVG from 'react-inlinesvg';
import plusSvg from 'assets/icons/plus.svg';
import arrowsOutLineHorizontalSvg from 'assets/icons/arrows_out_line_horizontal.svg';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';
import { ProductFieldName } from 'store/productManagement/reducer.types';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import SectionButton from 'lib/SectionButton';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import {
  changeNewProductField,
  hideSecondaryPanel,
  clearObjectivesInNewProduct,
  updateProductObjectives,
  addObjective,
} from 'store/productManagement/reducer';
import { changeActiveObjective } from 'store/objective/reducer';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { ObjectivesModal } from 'components/pages/Planner/PlannerSections/Objectives/ObjectivesModal/ObjectivesModal';
import { RowDataItem } from 'components/pages/Planner/PlannerSections/Objectives/ObjectivesModal/ObjectivesModal.types';
import { createDefaultObjective } from 'components/pages/Planner/PlannerSections/Objectives/useObjectives';
import { getIsReadOnly } from 'store/productManagement/selectors';
import {
  getNoRestrictionsDisplayValue,
  getValueRequiredPermissionDisplayValue,
} from '../../utils/getNoRestrictionsDisplayValue';
import { isValueRequired } from '../../utils/isValueRequired';
import ProductObjectivesPanel from './ProductObjectivesPanel';
import ProductObjectivePanelsWrapper from './ProductObjectivePanelsWrapper';

const fieldName = ProductFieldName.OBJECTIVES;

export const ProductObjectives: React.FC = () => {
  const dispatch = useDispatch();
  const onChangePanelVisibility = useOnChangePanelVisibility();
  const isReadOnly = useSelector(getIsReadOnly);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const objectiveValues = useSelector((state: Store) => state.productManagement.newProduct?.objectives?.value || []);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);

  const defaultSelectedPermission = useSelector(
    (state: Store) => state.productManagement.newProduct.objectives?.selectionPermission,
  );

  const isProductObjectivesOpen = secondaryPanelType === PlannerSecondaryPanelType.OBJECTIVE;
  const hasObjectives = objectiveValues.length > 0;
  const showInlineObjectives = isValueRequired(defaultSelectedPermission);

  const [isObjectivesModalOpen, setIsObjectivesModalOpen] = useState(false);

  const handleSubmit = ({ selectedPermission }: { selectedPermission: CodeNameModel }): void => {
    dispatch(
      changeNewProductField({
        fieldName,
        value: isValueRequired(selectedPermission.code) ? objectiveValues : [],
        selectedPermission,
      }),
    );
    dispatch(hideSecondaryPanel());

    if (isValueRequired(selectedPermission.code)) {
      const newObjective = createDefaultObjective(objectiveValues);
      dispatch(addObjective(newObjective));
      dispatch(changeActiveObjective(newObjective));
      dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.OBJECTIVE));
    }
  };

  const handleObjectivesModalSave = async (rowData: RowDataItem[]): Promise<void> => {
    dispatch(updateProductObjectives(rowData));
  };

  const onReviewObjectivesClick = (): void => {
    if (hasObjectives) setIsObjectivesModalOpen(true);
  };

  return (
    <>
      <div className="flex justify-end mb-1">
        <Tooltip tooltip="Review objectives" contentSize={TooltipSize.MAX} direction={TooltipDirection.TOP}>
          <button
            type="button"
            data-test-id="review-objectives-button"
            tabIndex={hasObjectives ? 0 : -1}
            className={cx('flex items-center gap-1 rounded-md leading-none align-middle p-2 w-8 h-8 justify-center', {
              'cursor-pointer bg-transparent text-primary-600 hover:bg-primary-50 active:bg-primary-200': hasObjectives,
              'cursor-not-allowed bg-neutral-300 text-neutral-400': !hasObjectives,
            })}
            onClick={onReviewObjectivesClick}
            onKeyDown={onReviewObjectivesClick}
            aria-label="Review objectives"
            aria-disabled={!hasObjectives}
          >
            <SVG src={arrowsOutLineHorizontalSvg} className="w-4 h-4 text-neutral-900" />
          </button>
        </Tooltip>
      </div>

      <SectionButton
        dataTestId="section-button-objective-permission"
        label="Objective"
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.OBJECTIVE)}
        theme={defaultSelectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED}
        icon={plusSvg}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        selected={
          isValueRequired(defaultSelectedPermission)
            ? getValueRequiredPermissionDisplayValue(defaultSelectedPermission)
            : getNoRestrictionsDisplayValue(defaultSelectedPermission)
        }
        onClear={() => {
          dispatch(clearObjectivesInNewProduct());
          dispatch(hideSecondaryPanel());
        }}
      />

      {showInlineObjectives && <ProductObjectivePanelsWrapper lineObjectives={objectiveValues} />}

      {isProductObjectivesOpen && <ProductObjectivesPanel onConfirm={handleSubmit} isReadOnly={isReadOnly} />}

      {isObjectivesModalOpen && (
        <ObjectivesModal
          isOpen={isObjectivesModalOpen}
          onClose={() => setIsObjectivesModalOpen(false)}
          objectives={objectiveValues}
          onSave={handleObjectivesModalSave}
        />
      )}
    </>
  );
};
